/**
 * Configurações › Sistema › **Monitoramento — padrões**.
 *
 * Pedido do Matheus em 02/09/2026:
 *
 * > *"preciso entrar em cada cliente para configurar os testes e falhas de cada
 * > coletor? não podemos deixar uma config padrão feita?"*
 *
 * ⚠️ **A herança em três níveis JÁ EXISTIA** — global → cliente → equipamento,
 * com `configEfetiva()` dizendo de onde veio cada valor. O valor global já
 * vivia no banco (`monitoramento_config`) e já havia rota para editá-lo. O que
 * faltava era esta tela. Medido antes de escrever qualquer linha.
 *
 * 🔴 **Mudar aqui alcança todo cliente que não sobrescreveu** — e a tela diz
 * QUANTOS antes de salvar. Mudar um número e atingir quarenta clientes sem
 * aviso é o tipo de coisa que ninguém desfaz com tranquilidade.
 */
import { useEffect, useState } from 'react';

import { Alert, Button, Card, HelpTip, Input, Label, Switch } from '@/components/ui';
import { api } from '@/lib/api';

import { LiberacaoColetorCard } from './LiberacaoColetorCard';

interface ConfigGlobal {
  intervaloSeg: number;
  falhasParaQueda: number;
  semNoticiaSeg: number;
  historicoDias: number;
  tetoClientesEmail: number;
  /** BLINDAGEM (560): horas contínuas reportando para a versão contar como provada. */
  canarioHoras: number;
}

interface Alcance {
  /** Clientes monitorados que NÃO sobrescreveram — os que este valor alcança. */
  herdam: number;
  /** Os que têm valor próprio e continuam com ele. */
  sobrescreveram: number;
}

const CAMPOS: Array<{
  chave: keyof ConfigGlobal;
  label: string;
  ajuda: string;
  min: number;
  max: number;
  sufixo: string;
}> = [
  {
    chave: 'intervaloSeg',
    label: 'Testar cada equipamento a cada',
    ajuda:
      'De quanto em quanto tempo o coletor testa cada equipamento. Menos tempo dá notícia mais rápida e gasta mais rede do cliente. 60 segundos é o padrão.',
    min: 10,
    max: 3600,
    sufixo: 'segundos',
  },
  {
    chave: 'falhasParaQueda',
    label: 'Declarar fora do ar após',
    ajuda:
      'Quantas falhas seguidas antes de dizer que o equipamento caiu. Um pacote perdido não é queda — por isso não é 1. Com 3 falhas e teste a cada 60s, o aviso sai em cerca de 3 minutos.',
    min: 1,
    max: 20,
    sufixo: 'falhas seguidas',
  },
  {
    chave: 'semNoticiaSeg',
    label: 'Sem notícia do coletor por',
    ajuda:
      'Quanto tempo sem o coletor falar até o sistema dizer "sem informação". Não é o mesmo que "fora do ar": sem notícia, os equipamentos podem estar perfeitos — só perdemos contato com quem os testa.',
    min: 60,
    max: 86400,
    sufixo: 'segundos',
  },
  {
    chave: 'historicoDias',
    label: 'Guardar o detalhe por hora por',
    ajuda:
      'Por quanto tempo fica o histórico hora a hora. Depois disso, sobra o resumo por dia, que fica para sempre — a disponibilidade de meses antigos continua respondida.',
    min: 7,
    max: 3650,
    sufixo: 'dias',
  },
  {
    chave: 'tetoClientesEmail',
    label: 'Acima deste número de clientes no mesmo evento, mandar um e-mail só',
    ajuda:
      'Se muitos clientes caírem juntos (uma operadora fora, por exemplo), vai um e-mail dizendo "queda ampla" em vez de um por cliente. Alerta que satura é alerta que se aprende a ignorar.',
    min: 1,
    max: 100,
    sufixo: 'clientes',
  },
  {
    chave: 'canarioHoras',
    label: 'Versão do coletor só entra em lote depois de reportar por',
    ajuda:
      'A trava da liberação em massa: uma versão nova precisa ficar reportando por este tempo contínuo em pelo menos um cliente antes de poder ir ao parque. O tempo importa: a 1.2.0 reportou o sucesso da própria instalação segundos antes de morrer — um critério de "já reportou" a teria deixado passar. Forçar continua possível, com confirmação.',
    min: 1,
    max: 720,
    sufixo: 'horas',
  },
];

interface MedicaoGlobal {
  tipos: Array<{ slug: string; nome: string; descricao: string; essencial: boolean; ligado: boolean }>;
  avisosEmailLigado: boolean;
  deteccoes: {
    oscilacaoJanelaHoras: number;
    oscilacaoQuedasMin: number;
    latenciaFatorAlerta: number;
    perdaPctAlerta: number;
  };
}

export function MonitoramentoPadroes() {
  const [cfg, setCfg] = useState<ConfigGlobal | null>(null);
  // OSCILACAO (557): a lista de tipos é DADO — tipo novo entra sem reforma de tela.
  const [medicao, setMedicao] = useState<MedicaoGlobal | null>(null);
  const [medicaoMsg, setMedicaoMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [alcance, setAlcance] = useState<Alcance | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  useEffect(() => {
    api
      .get<{ config: ConfigGlobal; alcance: Alcance; medicao?: MedicaoGlobal }>(
        '/api/v1/monitoramento/config',
      )
      .then((r) => {
        setCfg(r.config);
        setAlcance(r.alcance);
        setMedicao(r.medicao ?? null);
        setDraft(Object.fromEntries(CAMPOS.map((c) => [c.chave, String(r.config[c.chave])])));
      })
      .catch((e: Error) => setMsg(e.message));
  }, []);

  async function salvar() {
    // ⚠️ O formulário não é surdo: cada recusa aponta o CAMPO, não um toast.
    const novos: Record<string, string> = {};
    const corpo: Record<string, number> = {};
    for (const c of CAMPOS) {
      const v = Number(draft[c.chave]);
      if (!Number.isInteger(v) || v < c.min || v > c.max) {
        novos[c.chave] = `Informe um número inteiro entre ${c.min} e ${c.max}.`;
      } else {
        corpo[c.chave] = v;
      }
    }
    setErros(novos);
    if (Object.keys(novos).length) {
      setMsg(null);
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.patch<{ config: ConfigGlobal; alcance: Alcance }>(
        '/api/v1/monitoramento/config',
        corpo,
      );
      setCfg(r.config);
      setAlcance(r.alcance);
      setMsg(
        r.alcance.herdam === 0
          ? 'Salvo. Nenhum cliente monitorado usa estes padrões hoje.'
          : `Salvo — vale a partir do próximo ciclo em ${r.alcance.herdam} ${
              r.alcance.herdam === 1 ? 'cliente' : 'clientes'
            }.`,
      );
    } catch (e) {
      setMsg(`Não foi possível salvar: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!cfg) {
    return <div className="text-ink-3 dark:text-ink-dark-3">Carregando…</div>;
  }

  const mudou = CAMPOS.some((c) => String(cfg[c.chave]) !== draft[c.chave]);

  return (
    <div className="space-y-4">
      {/* 🔴 O alcance, dito ANTES de salvar. */}
      {alcance && (
        <Alert variant={alcance.herdam > 0 ? 'warning' : 'info'} rail title="Quem é afetado por estes valores">
          {alcance.herdam === 0 ? (
            <>Nenhum cliente monitorado usa estes padrões hoje — todos têm valores próprios, ou ainda não há coletor instalado.</>
          ) : (
            <>
              Mudar aqui alcança <strong>{alcance.herdam} {alcance.herdam === 1 ? 'cliente' : 'clientes'}</strong>{' '}
              que não têm valor próprio.
              {alcance.sobrescreveram > 0 && (
                <>
                  {' '}
                  Outros <strong>{alcance.sobrescreveram}</strong> configuraram valores na ficha e{' '}
                  <strong>continuam com o que escolheram</strong>.
                </>
              )}
            </>
          )}
        </Alert>
      )}

      <Card className="space-y-4 p-4">
        {CAMPOS.map((c) => (
          <div key={c.chave}>
            <Label htmlFor={`mon-${c.chave}`}>
              <span className="inline-flex items-center gap-1.5">
                {c.label}
                <HelpTip texto={c.ajuda} />
              </span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={`mon-${c.chave}`}
                type="number"
                min={c.min}
                max={c.max}
                value={draft[c.chave] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [c.chave]: e.target.value }))}
                error={erros[c.chave]}
                data-testid={`mon-padrao-${c.chave}`}
                className="max-w-[140px]"
              />
              <span className="text-sm text-ink-3 dark:text-ink-dark-3">{c.sufixo}</span>
            </div>
          </div>
        ))}

        <div className="flex items-center gap-3 pt-1">
          <Button loading={busy} disabled={!mudou} onClick={() => void salvar()} data-testid="mon-padrao-salvar">
            Salvar padrões
          </Button>
          {msg && <span className="text-sm text-ink-2 dark:text-ink-dark-2">{msg}</span>}
        </div>
      </Card>

      {medicao && (
        <Card className="space-y-3 p-4" data-testid="mon-tipos-medicao">
          <div>
            <h3 className="inline-flex items-center gap-1.5 font-medium text-ink-1 dark:text-ink-dark-1">
              O que o coletor mede
              <HelpTip texto="Desmarcar PARA DE MEDIR no cliente — a escolha viaja ao coletor no próximo ciclo, e poupar a rede do cliente é o ponto. O histórico do que já foi medido fica. Cada cliente pode ter exceção na ficha dele." />
            </h3>
            <p className="mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
              Um interruptor por tipo. Coletor de versão antiga ignora o que não entende e continua
              medindo a disponibilidade.
            </p>
          </div>
          {medicao.tipos.map((t) => (
            <div key={t.slug} className="flex items-start gap-2.5" data-testid={`mon-tipo-${t.slug}`}>
              <Switch
                checked={t.ligado}
                disabled={t.essencial}
                aria-label={t.nome}
                onChange={() => {
                  setMedicaoMsg(null);
                  api
                    .patch<{ medicao: MedicaoGlobal }>(
                      `/api/v1/monitoramento/tipos-medicao/${t.slug}`,
                      { ligado: !t.ligado },
                    )
                    .then((r) => setMedicao(r.medicao))
                    .catch((e: Error) => setMedicaoMsg(e.message));
                }}
              />
              <span className="min-w-0 text-sm">
                <span className="inline-flex items-center gap-1.5 font-medium text-ink-1 dark:text-ink-dark-1">
                  {t.nome}
                  <HelpTip texto={t.descricao} />
                </span>
                {t.essencial && (
                  <span className="block text-xs text-ink-3 dark:text-ink-dark-3">
                    É a base — desligar isto seria desligar o monitoramento. Não se desliga.
                  </span>
                )}
              </span>
            </div>
          ))}
          {/* 🔴 O interruptor do E-MAIL mudou de casa em 10/09 (pedido do
              Matheus): mora em Integrações › WhatsApp e avisos, junto dos
              outros avisos automáticos. Uma voz só — aqui fica o caminho. */}
          <p className="border-t border-line pt-3 text-xs text-ink-3 dark:border-line-dark dark:text-ink-dark-3">
            O liga/desliga do <strong>e-mail de avisos</strong> ({medicao.avisosEmailLigado ? 'ligado' : 'desligado'} agora) mora em{' '}
            <strong>Configurações › Integrações › WhatsApp e avisos › Os avisos</strong>, junto dos
            outros avisos automáticos.
          </p>
          {medicaoMsg && <Alert variant="danger">{medicaoMsg}</Alert>}
        </Card>
      )}

      {/* 559: a liberação em massa mora AQUI, junto dos interruptores — não
          numa tela nova. Só admin vê (o card se esconde no 403). */}
      <LiberacaoColetorCard />

      <p className="text-xs text-ink-3 dark:text-ink-dark-3">
        Cada cliente pode ter valores próprios na aba Monitoramento da ficha dele, e cada
        equipamento pode ter os dele. O valor mais específico ganha: equipamento, depois cliente,
        depois este padrão. A ficha mostra de onde veio cada número.
      </p>
    </div>
  );
}
